/**
 * @description Component-CoinPriceChart
 */
import './index.less';

import { FundingChartInterval, KlineInterval } from '@synfutures/sdks-perp-datasource';
import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Card from '@/components/Card';
import { CHART_TYPE } from '@/constants/trade';
import { getPairKlineData } from '@/features/chart/actions';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { setFundingChartDuration, setPairChartDuration } from '@/features/trade/actions';
import { useChartDuration, useFundingChartDuration } from '@/features/trade/hooks';
import { useAppDispatch } from '@/hooks';
import { useChainId } from '@/hooks/web3/useChain';

import ChartInfo from './ChartInfo';
import FundingChart from './FundingChart';
// import CoinPriceDetail from './CoinPriceDetail';
import SynCircleTabs from '@/components/Tabs/SynCircleTabs';
import SynTitleTabs from '@/components/Tabs/SynTitleTabs';
import { useBackendChainConfig } from '@/features/config/hook';
import TvChart from '@/pages/components/TradingViewPro';
import PriceDepthChart from './PriceDepthChart';
// import CoinPriceDepthChart from './CoinPriceDepthChart';
// import PriceDepthChart from './PriceDepthChart';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
}
const CoinPriceChart: FC<IPropTypes> = function ({ className = '' }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const dappConfig = useBackendChainConfig(chainId);
  const [tab, setTab] = useState(CHART_TYPE.PRICE);
  const chartDuration = useChartDuration(chainId);
  const fundingChartDuration = useFundingChartDuration(chainId);
  const setChartDuration = useCallback(
    (duration: KlineInterval) => {
      chainId && dispatch(setPairChartDuration({ chainId, chartDuration: duration }));
    },
    [chainId, dispatch],
  );
  const setFundingChartDurationCB = useCallback(
    (duration: FundingChartInterval) => {
      chainId && dispatch(setFundingChartDuration({ chainId, chartDuration: duration }));
    },
    [chainId, dispatch],
  );

  const currentPair = useCurrentPairFromUrl(chainId);

  const tabList = useMemo(
    () => [
      { label: t('common.tradePage.price'), vale: CHART_TYPE.PRICE, key: CHART_TYPE.PRICE },
      { label: t('common.tradePage.depth'), vale: CHART_TYPE.DEPTH, key: CHART_TYPE.DEPTH },
      { label: t('common.tradePage.funding'), vale: CHART_TYPE.FUNDING, key: CHART_TYPE.FUNDING },
      { label: t('common.tradePage.info'), vale: CHART_TYPE.INFO, key: CHART_TYPE.INFO },
    ],
    [t],
  );
  useEffect(() => {
    const defaultDuration =
      (dappConfig?.trade.fundingChartInterval.default as FundingChartInterval) || FundingChartInterval.HOUR;
    setFundingChartDurationCB(defaultDuration);
    return () => {
      setFundingChartDurationCB(defaultDuration);
    };
  }, [dappConfig?.trade.fundingChartInterval.default, setFundingChartDurationCB]);
  useEffect(() => {
    const defaultDuration = (dappConfig?.trade.klineInterval.default as KlineInterval) || KlineInterval.HOUR;
    setChartDuration(defaultDuration);
    return () => {
      const defaultDuration = (dappConfig?.trade.klineInterval.default as KlineInterval) || KlineInterval.HOUR;
      setChartDuration(defaultDuration);
    };
  }, [chainId, dappConfig?.trade.klineInterval.default, setChartDuration, currentPair]);

  useEffect(() => {
    if (currentPair && chainId) {
      try {
        dispatch(
          getPairKlineData({
            chainId,
            instrumentAddr: currentPair.rootInstrument.id,
            expiry: currentPair.expiry,
            chartDuration,
          }),
        );
      } catch (e) {
        console.log('🚀 ~ file: index.tsx:122 ~ useEffect ~ e:', e);
      }
    }
  }, [currentPair?.id, chainId, chartDuration]);

  useEffect(() => {
    setTab(CHART_TYPE.PRICE);
  }, [chainId, currentPair?.id]);

  return (
    <Card className={classNames(className, 'syn-trade-charts')}>
      <>
        <div className="syn-trade-charts-content">
          <div className="syn-trade-charts-content-price">
            <>
              <div className="syn-trade-charts-content-title">
                <SynTitleTabs
                  tabsStyle={{ gap: 16 }}
                  varient="solid"
                  activeKey={tab}
                  onClick={(key) => setTab(key as CHART_TYPE)}
                  items={tabList}
                />

                {tab === CHART_TYPE.FUNDING && (
                  <SynCircleTabs
                    varient="solid"
                    className="syn-coin-price-right-tabs-FUNDING"
                    items={
                      dappConfig?.trade.fundingChartInterval.list.map((interval: string) => {
                        return {
                          label: t(`common.tradePage.${interval}`),
                          key: interval,
                        };
                      }) || []
                    }
                    activeKey={fundingChartDuration}
                    onClick={(key) => {
                      setFundingChartDurationCB(key as FundingChartInterval);
                    }}
                  />
                )}
              </div>
              <div className="syn-trade-charts-content-container">
                {/* {fetchingStatus === FETCHING_STATUS.DONE && !kLineDataList?.length && tab !== CHART_TYPE.INFO && (
                <div className="syn-trade-charts-empty">{t('common.nodata')}</div>
              )} */}

                {tab === CHART_TYPE.PRICE && <TvChart />}
                {tab === CHART_TYPE.FUNDING && (
                  <FundingChart chainId={chainId} currentPair={currentPair} chartDuration={fundingChartDuration} />
                )}
                {tab === CHART_TYPE.DEPTH && <PriceDepthChart pair={currentPair} />}

                {tab === CHART_TYPE.INFO && <ChartInfo />}
              </div>
            </>
          </div>
          {/* <OrderBookSection currentPair={currentPair} /> */}
        </div>
      </>
    </Card>
  );
};

export default CoinPriceChart;
